import { useState, useEffect } from 'react';
import { Bell, Calendar, Clock, Moon, Sun } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';

interface HeaderProps {
  title: string;
  subtitle?: string;
  notificationCount?: number;
}

export const Header = ({ title, subtitle, notificationCount = 0 }: HeaderProps) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <header className="h-18 bg-white dark:bg-gray-900 border-b-2 border-gray-300 dark:border-gray-700 shadow-sm px-8 flex items-center justify-between transition-colors">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
        {subtitle && <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
      </div>

      <div className="flex items-center gap-6">
        {/* Date & Time */}
        <div className="flex items-center gap-4 text-sm bg-gray-50 dark:bg-gray-800 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Calendar className="w-4 h-4" />
            <span className="capitalize">{formatDate(currentTime)}</span>
          </div>
          <div className="flex items-center gap-2 font-mono font-bold text-gray-900 dark:text-white">
            <Clock className="w-4 h-4" />
            <span>{formatTime(currentTime)}</span>
          </div>
        </div>

        {/* Notifications */}
        <button className="relative p-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-600">
          <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          {notificationCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md">
              {notificationCount}
            </span>
          )}
        </button>
      </div>
    </header>
  );
};
